/**
 * Shift Management Configuration
 * 
 * This file contains configurations for the shift scheduling system.
 */

// Users with edit permissions for shift management
// Only these users can create, update, and delete shift assignments
export const SHIFT_EDIT_PERMISSIONS = {
  // Example: Add usernames of users who have permission to edit shifts
  users: [
    'mmariani',
    'mcuyler',
    'plazzarin',
    'jgantt',
    'ykumar',
    'cpaz',
    'jamirzada'
  ],
};

// Time off types
export const TIME_OFF_TYPES = [
  { value: 'vacation', label: 'Vacation' },
  { value: 'sick_leave', label: 'Sick Leave' },
  { value: 'personal', label: 'Personal Day' },
  { value: 'other', label: 'Other' },
];

// Team shift mapping - shifts are filtered by team prefix
// IT* = Italy NOC, US* = USA NOC, IN* or Weekend = India NOC
export const TEAM_SHIFT_MAPPING = {
  'USA side': { prefix: 'US', label: '🇺🇸 USA' },
  'Italian side': { prefix: 'IT', label: '🇮🇹 Italy' },
  'India side': { prefix: 'IN', label: '🇮🇳 India', includeWeekend: true },
};

// Default shift configuration
export const DEFAULT_SHIFTS = [
  { name: '23:00-00:00', start_time: '23:00:00', end_time: '00:00:00' },
  { name: '00:00-01:00', start_time: '00:00:00', end_time: '01:00:00' },
  { name: '01:00-02:00', start_time: '01:00:00', end_time: '02:00:00' },
  { name: '02:00-03:00', start_time: '02:00:00', end_time: '03:00:00' },
  { name: '03:00-04:00', start_time: '03:00:00', end_time: '04:00:00' },
  { name: '04:00-05:00', start_time: '04:00:00', end_time: '05:00:00' },
  { name: '05:00-06:00', start_time: '05:00:00', end_time: '06:00:00' },
  { name: '06:00-07:00', start_time: '06:00:00', end_time: '07:00:00' },
  { name: '07:00-08:00', start_time: '07:00:00', end_time: '08:00:00' },
  { name: '08:00-09:00', start_time: '08:00:00', end_time: '09:00:00' },
  { name: '09:00-10:00', start_time: '09:00:00', end_time: '10:00:00' },
  { name: '10:00-11:00', start_time: '10:00:00', end_time: '11:00:00' },
  { name: '11:00-12:00', start_time: '11:00:00', end_time: '12:00:00' },
  { name: '12:00-13:00', start_time: '12:00:00', end_time: '13:00:00' },
  { name: '13:00-14:00', start_time: '13:00:00', end_time: '14:00:00' },
  { name: '14:00-15:00', start_time: '14:00:00', end_time: '15:00:00' },
  { name: '15:00-16:00', start_time: '15:00:00', end_time: '16:00:00' },
  { name: '16:00-17:00', start_time: '16:00:00', end_time: '17:00:00' },
  { name: '17:00-18:00', start_time: '17:00:00', end_time: '18:00:00' },
  { name: '18:00-19:00', start_time: '18:00:00', end_time: '19:00:00' },
  { name: '19:00-20:00', start_time: '19:00:00', end_time: '20:00:00' },
  { name: '20:00-21:00', start_time: '20:00:00', end_time: '21:00:00' },
  { name: '21:00-22:00', start_time: '21:00:00', end_time: '22:00:00' },
  { name: '22:00-23:00', start_time: '22:00:00', end_time: '23:00:00' },
];

// Teams (Sides)
export const DEFAULT_TEAMS = [
  { name: 'USA side', description: 'USA monitoring team' },
  { name: 'Italian side', description: 'Italian monitoring team' },
  { name: 'India side', description: 'India monitoring team' },
];

// Holiday configuration
// Format: { country: 'Italy|India|USA', date: 'YYYY-MM-DD', name: 'Holiday Name' }
export const HOLIDAYS = [
  { country: 'Italy', date: '2026-01-01', name: 'New Year' },
  { country: 'Italy', date: '2026-04-06', name: 'Easter Monday' },
  { country: 'Italy', date: '2026-05-01', name: 'Labour Day' },
  { country: 'Italy', date: '2026-06-02', name: 'Republic Day' },
  { country: 'Italy', date: '2026-10-07', name: 'Castel San Pietro Protector' },
  { country: 'Italy', date: '2026-12-08', name: 'Immaculate Conception' },
  { country: 'Italy', date: '2026-12-25', name: 'Christmas Day' },
  
  { country: 'USA', date: '2026-01-01', name: 'New Year' },
  { country: 'USA', date: '2026-04-03', name: 'Good Friday' },
  { country: 'USA', date: '2026-05-25', name: 'Memorial Day' },
  { country: 'USA', date: '2026-07-03', name: 'Independence Day' },
  { country: 'USA', date: '2026-09-07', name: 'Labor Day' },
  { country: 'USA', date: '2026-11-26', name: 'Thanksgiving' },
  { country: 'USA', date: '2026-11-27', name: 'Black Friday' },
  { country: 'USA', date: '2026-12-25', name: 'Christmas Day' },
  
  { country: 'India', date: '2026-01-26', name: 'Republic Day' },
  { country: 'India', date: '2026-03-04', name: 'Holi' },
  { country: 'India', date: '2026-04-03', name: 'Good Friday' },
  { country: 'India', date: '2026-05-01', name: 'Labour Day' },
  { country: 'India', date: '2026-05-26', name: 'Eid ul-Zuha' },
  { country: 'India', date: '2026-09-14', name: 'Ganesh Chaturthi' },
  { country: 'India', date: '2026-10-02', name: 'Gandhi Jayanti' },
  { country: 'India', date: '2026-10-20', name: 'Dussehra' },
  { country: 'India', date: '2026-11-09', name: 'Deepavali' },
  { country: 'India', date: '2026-12-25', name: 'Christmas Day' },
];

// Country flag mapping
export const COUNTRY_FLAGS = {
  'Italy': '🇮🇹',
  'USA': '🇺🇸',
  'India': '🇮🇳',
};

const shiftConfig = {
  SHIFT_EDIT_PERMISSIONS,
  TIME_OFF_TYPES,
  DEFAULT_SHIFTS,
  DEFAULT_TEAMS,
  TEAM_SHIFT_MAPPING,
  HOLIDAYS,
  COUNTRY_FLAGS,
};

export default shiftConfig;
